import React, { useContext, useEffect, useState, useCallback } from "react";
import { Tooltip, Typography, Form, Input, Radio, Select, Space, Button as AntButton, message } from "antd";
import { Button, Offcanvas } from "react-bootstrap";
import { CustomerProps } from "../ShowCustomer";
import { usePastPurchaseFeedbackStore } from "./store";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const TIME_RANGE_OPTIONS = [
    "8:00 to 10:00",
    "9:00 to 11:00",
    "10:00 to 12:00",
    "11:00 to 13:00",
    "12:00 to 14:00",
    "13:00 to 15:00",
    "14:00 to 16:00",
    "15:00 to 17:00",
    "16:00 to 18:00",
    "17:00 to 19:00",
    "18:00 to 20:00"
];

const PostPurchaseFeedback = () => {
    const [show, setShow] = useState(false);
    const [form] = Form.useForm();
    const [isEditing, setIsEditing] = useState(false);
    const [slotBooked, setSlotBooked] = useState(true);
    const [formInitialized, setFormInitialized] = useState(false);
    const [submitting, setSubmitting] = useState(false); // Local loading state for form submission

    const { customer } = useContext(CustomerProps);

    const {
        feedbackData,
        loading,
        error,
        getOnePostPurchaseFeedback,
        createPostPurchaseFeedback,
        updatePostPurchaseFeedback,
        clearFeedback
    } = usePastPurchaseFeedbackStore();

    // Handle modal open/close
    const handleClose = useCallback(() => setShow(false), []);
    const handleShow = useCallback(() => setShow(true), []);
    const handleEdit = useCallback(() => setIsEditing(true), []);

    // Fetch feedback data when modal is opened
    useEffect(() => {
        const fetchData = async () => {
            if (show && customer?.id) {
                try {
                    await getOnePostPurchaseFeedback(customer.id);
                } catch (error) {
                    console.error("No existing feedback found:", error);
                } finally {
                    setFormInitialized(true);
                }
            }
        };

        fetchData();

        // Clear store data when modal is closed
        return () => {
            if (!show) {
                form.resetFields();
                clearFeedback();
                setFormInitialized(false);
                setIsEditing(false);
            }
        };
    }, [show, customer, getOnePostPurchaseFeedback, clearFeedback, form]);

    // Set form values when feedback data is loaded
    useEffect(() => {
        if (feedbackData) {
            form.setFieldsValue({
                appUsage: feedbackData.app_usage,
                ivrNumberSaved: feedbackData.ivr_number_saved,
                reviewOnGoogle: feedbackData.review_on_google,
                kitMarkingExplained: feedbackData.kit_marking_explained,
                travellingGuidanceExplained: feedbackData.travelling_guidance_explained,
                bookASlotExplained: feedbackData.book_a_slot_explained,
                reasonIfNotBooked: feedbackData.reason_if_not_booked,
                followUpCallExplained: feedbackData.follow_up_call_explained,
                customerPreferredSlot: feedbackData.customer_preferred_slot,
            });

            setSlotBooked(feedbackData.book_a_slot_explained);
        }
    }, [feedbackData, form]);

    // Handle cancellation of editing
    const handleCancel = useCallback(() => {
        if (feedbackData) {
            // Reset form to the original data
            form.setFieldsValue({
                appUsage: feedbackData.app_usage,
                ivrNumberSaved: feedbackData.ivr_number_saved,
                reviewOnGoogle: feedbackData.review_on_google,
                kitMarkingExplained: feedbackData.kit_marking_explained,
                travellingGuidanceExplained: feedbackData.travelling_guidance_explained,
                bookASlotExplained: feedbackData.book_a_slot_explained,
                reasonIfNotBooked: feedbackData.reason_if_not_booked,
                followUpCallExplained: feedbackData.follow_up_call_explained,
                customerPreferredSlot: feedbackData.customer_preferred_slot,
            });
            setSlotBooked(feedbackData.book_a_slot_explained);
        } else {
            form.resetFields();
        }
        setIsEditing(false);
    }, [feedbackData, form]);

    // Form submission handler
    const onFinish = useCallback(async (values) => {
        // Use local submitting state instead of relying on store loading state
        setSubmitting(true);
        
        try {
            if (feedbackData) {
                await updatePostPurchaseFeedback(customer.id, values);
                message.success("Feedback updated successfully");
            } else {
                await createPostPurchaseFeedback(customer.id, values);
                message.success("Feedback created successfully");
            }
            setIsEditing(false);
        } catch (err) {
            console.error("Error in form submission:", err);
        } finally {
            // Always reset submitting state regardless of success or failure
            setSubmitting(false);
        }
    }, [feedbackData, customer, updatePostPurchaseFeedback, createPostPurchaseFeedback]);

    // Handle slot booking status change
    const handleSlotBookedChange = useCallback((e) => {
        setSlotBooked(e.target.value);
    }, []);

    return (
        <div>
            <Tooltip title="Fill Post Purchase Feedback Form">
                <Button variant="primary" className="py-2 me-2" onClick={handleShow}>
                    Post Purchase Feedback
                </Button>
            </Tooltip>

            <Offcanvas
                placement="end"
                show={show}
                onHide={handleClose}
                style={{ width: "70%" }}
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title className="m-0">
                        <Title level={3} className="text-primary fw-bolder m-0">Post Purchase Feedback Form</Title>
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="pt-0">
                    {(loading && !formInitialized) ? (
                        <div className="text-center p-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            {error && error !== "Post purchase feedback not found" && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}

                            {!isEditing && feedbackData && (
                                <div className="text-end mb-3">
                                    <AntButton type="primary" onClick={handleEdit}>
                                        Edit Feedback
                                    </AntButton>
                                </div>
                            )}

                            <Form
                                form={form}
                                layout="vertical"
                                onFinish={onFinish}
                                disabled={!isEditing && feedbackData}
                                initialValues={{
                                    appUsage: undefined,
                                    ivrNumberSaved: undefined,
                                    reviewOnGoogle: undefined,
                                    kitMarkingExplained: undefined,
                                    travellingGuidanceExplained: undefined,
                                    bookASlotExplained: undefined,
                                    followUpCallExplained: undefined,
                                    customerPreferredSlot: [],
                                    reasonIfNotBooked: '',
                                }}
                            >
                                <div className="row">
                                    <div className="col-md-6">
                                        <Form.Item
                                            name="appUsage"
                                            label="App Usage Explained"
                                            rules={[{ required: true, message: 'Please select an option' }]}
                                        >
                                            <Radio.Group>
                                                <Radio value={true}>Yes</Radio>
                                                <Radio value={false}>No</Radio>
                                            </Radio.Group>
                                        </Form.Item>
                                    </div>
                                    <div className="col-md-6">
                                        <Form.Item
                                            name="ivrNumberSaved"
                                            label="IVR Number Saved"
                                            rules={[{ required: true, message: 'Please select an option' }]}
                                        >
                                            <Radio.Group>
                                                <Radio value={true}>Yes</Radio>
                                                <Radio value={false}>No</Radio>
                                            </Radio.Group>
                                        </Form.Item>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <Form.Item
                                            name="reviewOnGoogle"
                                            label="Review on Google Explained"
                                            rules={[{ required: true, message: 'Please select an option' }]}
                                        >
                                            <Radio.Group>
                                                <Radio value={true}>Yes</Radio>
                                                <Radio value={false}>No</Radio>
                                            </Radio.Group>
                                        </Form.Item>
                                    </div>
                                    <div className="col-md-6">
                                        <Form.Item
                                            name="kitMarkingExplained"
                                            label="Kit Marking Explained"
                                            rules={[{ required: true, message: 'Please select an option' }]}
                                        >
                                            <Radio.Group>
                                                <Radio value={true}>Yes</Radio>
                                                <Radio value={false}>No</Radio>
                                            </Radio.Group>
                                        </Form.Item>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <Form.Item
                                            name="travellingGuidanceExplained"
                                            label="Travelling Guidance Explained"
                                            rules={[{ required: true, message: 'Please select an option' }]}
                                        >
                                            <Radio.Group>
                                                <Radio value={true}>Yes</Radio>
                                                <Radio value={false}>No</Radio>
                                            </Radio.Group>
                                        </Form.Item>
                                    </div>
                                    <div className="col-md-6">
                                        <Form.Item
                                            name="bookASlotExplained"
                                            label="Book a Slot Explained"
                                            rules={[{ required: true, message: 'Please select an option' }]}
                                        >
                                            <Radio.Group onChange={handleSlotBookedChange}>
                                                <Radio value={true}>Yes</Radio>
                                                <Radio value={false}>No</Radio>
                                            </Radio.Group>
                                        </Form.Item>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <Form.Item
                                            name="followUpCallExplained"
                                            label="Follow-up Call Explained"
                                            rules={[{ required: true, message: 'Please select an option' }]}
                                        >
                                            <Radio.Group>
                                                <Radio value={true}>Yes</Radio>
                                                <Radio value={false}>No</Radio>
                                            </Radio.Group>
                                        </Form.Item>
                                    </div>
                                    <div className="col-md-6">
                                        <Form.Item
                                            name="customerPreferredSlot"
                                            label="Customer Preferred Time Ranges"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: 'Please select at least one preferred time range',
                                                },
                                            ]}
                                        >
                                            <Select
                                                mode="multiple"
                                                placeholder="Select preferred time ranges"
                                                style={{ width: '100%' }}
                                            >
                                                {TIME_RANGE_OPTIONS.map(timeRange => (
                                                    <Option key={timeRange} value={timeRange}>{timeRange}</Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </div>
                                </div>

                                {!slotBooked && (
                                    <Form.Item
                                        name="reasonIfNotBooked"
                                        label="Reason for Slot Not Booked"
                                        rules={[
                                            {
                                                required: !slotBooked,
                                                message: 'Please provide a reason why the slot was not booked'
                                            }
                                        ]}
                                    >
                                        <TextArea rows={4} placeholder="Enter reason for slot was not booked" />
                                    </Form.Item>
                                )}

                                {feedbackData && (
                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <Text type="secondary">Created: {new Date(feedbackData.created_at).toLocaleString()}</Text>
                                        </div>
                                        <div className="col-md-6 text-end">
                                            <Text type="secondary">Last Updated: {new Date(feedbackData.updated_at).toLocaleString()}</Text>
                                        </div>
                                    </div>
                                )}

                                {isEditing && (
                                    <div className="text-end">
                                        <Space>
                                            <AntButton onClick={handleCancel} disabled={submitting}>
                                                Cancel
                                            </AntButton>
                                            <AntButton type="primary" htmlType="submit" loading={submitting}>
                                                Save
                                            </AntButton>
                                        </Space>
                                    </div>
                                )}

                                {!feedbackData && !isEditing && formInitialized && (
                                    <div className="text-end">
                                        <AntButton type="primary" onClick={handleEdit}>
                                            Create Feedback
                                        </AntButton>
                                    </div>
                                )}
                            </Form>
                        </>
                    )}
                </Offcanvas.Body>
            </Offcanvas>
        </div>
    );
};

export default PostPurchaseFeedback;